import { useRateFieldError } from "../../hooks/useRateFieldError";
import { FC } from "react";

import { TicketingFormCategory } from "@simplyk/common";
import { useFormContext } from "react-hook-form";

import { useRateContext } from "../../Context/RateContext";
import { useRateFieldName } from "../../hooks/useRateFieldName";

import { isRequired } from "@/common/helpers/validators";
import { useFormInputChanged } from "@/common/hooks/useFormInputChanged";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

const MAX_LENGTH_TITLE = 60;

interface NameFieldProps {
  label?: string;
  placeholder?: string;
  onValueChange?: (value: string) => void;
}

export const NameField: FC<NameFieldProps> = ({ label: labelProp, placeholder: placeholderProp, onValueChange }) => {
  const { t } = useTranslate();
  const { rateIndex, formCategory, isEditing, isOnboarding } = useRateContext();
  const { control, getValues } = useFormContext<StepTicketsFormData>();
  const { formInputChangedCallback } = useFormInputChanged();

  const fieldName = useRateFieldName("name");
  const error = useRateFieldError("name");

  const isShop = formCategory === TicketingFormCategory.Shop;
  const isCustom = formCategory === TicketingFormCategory.Custom;
  const isAuction = formCategory === TicketingFormCategory.Auction;

  const labelKey =
    isAuction || isShop ? "ticketing.rateName.item" : isCustom ? "ticketing.rateName.rate" : "ticketing.rateName";
  const label = labelProp ?? t("dashboard", labelKey);
  const placeholder =
    placeholderProp ??
    (isAuction
      ? t("dashboard", "ticketing.auctionItemPlaceholder")
      : isCustom
        ? t("dashboard", "ticketing.customItemPlaceholder")
        : "");
  const requiredMessage = t(
    "dashboard",
    isShop ? "ticketing.rateNameIsRequiredShop" : "ticketing.rateNameIsRequired"
  ) as string;

  return (
    <DashboardTextField
      data-test="rate-name-input"
      autoFocus={rateIndex === 0}
      debounced={250}
      name={fieldName}
      label={label}
      placeholder={placeholder}
      control={control}
      errorMessage={error?.message}
      rules={[isRequired(requiredMessage)]}
      inputProps={{ maxLength: MAX_LENGTH_TITLE }}
      onChange={onValueChange ? (event) => onValueChange(event.target.value) : undefined}
      onBlur={formInputChangedCallback({
        input_name: "rate_name",
        form_type: formCategory,
        field_empty: !getValues(fieldName),
        valid_field: !error,
        is_editing: isEditing,
        is_onboarding: isOnboarding,
      })}
    />
  );
};
